import React from 'react';
import { StepProps } from '../MultiStepModal';

export default function CertificateTemplateFieldsStep({ data, onDataChange }: StepProps) {
  const fields: any[] = Array.isArray(data.fields) ? data.fields : [];

  const fieldOrdering: 'row-major' | 'column-major' =
    (data as any)?.fieldOrdering === 'column-major' ? 'column-major' : 'row-major';

  const parseContextLabel = (label: string) => {
    const s = String(label || '').replace(/\s+/g, ' ').trim();
    const m = s.match(/^([A-Za-z][A-Za-z ]{1,30})\s*(\d{1,2})\b\s*(.*)$/);
    if (!m) return null;
    return {
      base: String(m[1] || '').replace(/\s+/g, ' ').trim(),
      n: Number(m[2]),
      rest: String(m[3] || '').replace(/\s+/g, ' ').trim(),
    };
  };

  const sortExtractedFields = (arr: any[], strategy: 'row-major' | 'column-major') => {
    const withIdx = arr.map((f, idx) => ({ f, idx }));

    const numOrNull = (v: any) => {
      if (typeof v === 'number' && Number.isFinite(v)) return v;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const pageOrZero = (v: any) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

    withIdx.sort((a, b) => {
      const ap = pageOrZero(a.f.pageIndex);
      const bp = pageOrZero(b.f.pageIndex);
      if (ap !== bp) return ap - bp;

      const ax = numOrNull(a.f.x);
      const bx = numOrNull(b.f.x);
      const ay = numOrNull(a.f.y);
      const by = numOrNull(b.f.y);

      const hasGeomA = ax !== null && ay !== null;
      const hasGeomB = bx !== null && by !== null;
      if (hasGeomA && !hasGeomB) return -1;
      if (!hasGeomA && hasGeomB) return 1;

      if (hasGeomA && hasGeomB) {
        if (strategy === 'column-major') {
          if (ax !== bx) return (ax as number) - (bx as number);
          if (ay !== by) return (by as number) - (ay as number);
        } else {
          if (ay !== by) return (by as number) - (ay as number);
          if (ax !== bx) return (ax as number) - (bx as number);
        }
      }

      const ac = parseContextLabel(String(a.f.displayLabel || ''));
      const bc = parseContextLabel(String(b.f.displayLabel || ''));
      if (ac && bc && ac.base && bc.base && ac.base === bc.base) {
        if (strategy === 'column-major') {
          if (ac.n !== bc.n) return ac.n - bc.n;
          if (ac.rest !== bc.rest) return ac.rest.localeCompare(bc.rest);
        } else {
          if (ac.rest !== bc.rest) return ac.rest.localeCompare(bc.rest);
          if (ac.n !== bc.n) return ac.n - bc.n;
        }
      }

      return a.idx - b.idx;
    });

    return withIdx.map((w) => w.f);
  };

  const updateField = (idx: number, patch: any) => {
    const next = [...fields];
    next[idx] = { ...next[idx], ...patch, orderIndex: next[idx]?.orderIndex ?? idx };
    onDataChange({ fields: next });
  };

  const setOrdering = (ordering: 'row-major' | 'column-major') => {
    const sorted = sortExtractedFields(fields, ordering);
    const next = sorted.map((f: any, idx: number) => ({ ...f, orderIndex: idx }));
    onDataChange({ fieldOrdering: ordering, fields: next });
  };

  const geometryCount = fields.filter(
    (f: any) =>
      (typeof f?.x === 'number' && Number.isFinite(f.x)) ||
      (Number.isFinite(Number(f?.x)) && Number.isFinite(Number(f?.y))),
  ).length;

  const inferGroupName = (field: any) => {
    const persisted = String(field?.groupName || '').replace(/\s+/g, ' ').trim();
    if (persisted) return persisted;

    const label = String(field?.displayLabel || '').trim();
    if (!label) return null;
    const m = label.match(/^([A-Za-z][A-Za-z ]{1,30})\s*(\d{1,2})\b/);
    if (!m) return null;
    const base = String(m[1] || '').replace(/\s+/g, ' ').trim();
    const num = String(m[2] || '').trim();
    if (!base || !num) return null;
    return `${base} ${num}`;
  };

  const grouped = (() => {
    const items = fields.map((f: any, idx: number) => ({ f, idx }));
    const rawGroups = new Map<string, Array<{ f: any; idx: number }>>();

    for (const it of items) {
      const groupName = inferGroupName(it.f);
      if (!groupName) continue;
      const existing = rawGroups.get(groupName) ?? [];
      existing.push(it);
      rawGroups.set(groupName, existing);
    }

    const eligibleGroupNames = new Set(
      Array.from(rawGroups.entries())
        .filter(([, v]) => v.length >= 3)
        .map(([k]) => k),
    );

    const byGroup = new Map<string, Array<{ f: any; idx: number }>>();
    const ungrouped: Array<{ f: any; idx: number }> = [];

    for (const it of items) {
      const name = inferGroupName(it.f);
      if (name && eligibleGroupNames.has(name)) {
        const existing = byGroup.get(name) ?? [];
        existing.push(it);
        byGroup.set(name, existing);
      } else {
        ungrouped.push(it);
      }
    }

    const groups = Array.from(byGroup.entries()).map(([name, list]) => ({
      name,
      items: list,
    }));

    groups.sort((a, b) => {
      const aMin = Math.min(...a.items.map((x) => x.f?.orderIndex ?? x.idx));
      const bMin = Math.min(...b.items.map((x) => x.f?.orderIndex ?? x.idx));
      return aMin - bMin;
    });

    if (ungrouped.length) {
      groups.push({ name: 'Other fields', items: ungrouped });
    }

    return groups;
  })();

  if (fields.length === 0) {
    return (
      <div className="step-form">
        <div className="step-header">
          <h3 className="step-form-title">Template fields</h3>
          <p className="step-form-subtitle">No extracted fields yet. Go back and extract fields from your PDF.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="step-form">
      <div className="step-header">
        <h3 className="step-form-title">Template fields</h3>
        <p className="step-form-subtitle">Rename fields and mark what’s required.</p>
      </div>

      <div className="step-form-content">
        <div className="mb-4 rounded-xl border border-[#E6E6E4] bg-[#FAFAF9] p-4">
          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#9CA3AF]">Fill order</div>
          <div className="mt-2 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-[#383838]">
              Choose how engineers will fill repeating table/grid fields.
            </div>
            <select
              className="h-9 rounded-lg border border-[#E6E6E4] bg-white px-3 text-sm"
              value={fieldOrdering}
              onChange={(e) => setOrdering(e.target.value as any)}
            >
              <option value="row-major">Question-by-question (top-to-bottom)</option>
              <option value="column-major">Appliance-by-appliance (left-to-right columns)</option>
            </select>
          </div>
          <div className="mt-2 text-xs text-[#686E6C]">
            Geometry available for {geometryCount}/{fields.length} fields. If this stays 0, go back and re-extract.
          </div>
        </div>

        <div className="space-y-6">
          {grouped.map((g: any) => (
            <div key={g.name}>
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-semibold text-[#181818]">{g.name}</div>
                <div className="text-xs text-[#686E6C]">{g.items.length} fields</div>
              </div>

              <div className="space-y-4">
                {g.items.map(({ f, idx }: any) => (
                  <div
                    key={f.pdfFieldName || idx}
                    className="rounded-xl border border-[#E6E6E4] bg-white p-4"
                  >
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="min-w-0">
                        <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#9CA3AF]">Field</div>
                        <div className="mt-1 text-sm font-semibold text-[#181818] truncate">
                          {f.displayLabel || 'Untitled field'}
                        </div>
                        <div className="mt-1 text-xs text-[#686E6C] truncate">PDF: {f.pdfFieldName}</div>
                      </div>

                      <div className="flex items-center gap-3">
                        <label className="inline-flex items-center gap-2 text-sm text-[#383838]">
                          <input
                            type="checkbox"
                            checked={Boolean(f.required)}
                            onChange={(e) => updateField(idx, { required: e.target.checked })}
                          />
                          Required
                        </label>

                        <select
                          className="h-9 rounded-lg border border-[#E6E6E4] bg-white px-3 text-sm"
                          value={f.fieldType || 'text'}
                          onChange={(e) => updateField(idx, { fieldType: e.target.value })}
                        >
                          <option value="text">Text</option>
                          <option value="checkbox">Checkbox</option>
                          <option value="radio">Radio</option>
                          <option value="select">Select</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="form-group">
                        <label className="form-label">Label</label>
                        <input
                          className="form-input"
                          value={f.displayLabel || ''}
                          onChange={(e) => updateField(idx, { displayLabel: e.target.value })}
                          placeholder="Tenant name"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Hint</label>
                        <input
                          className="form-input"
                          value={f.hint || ''}
                          onChange={(e) => updateField(idx, { hint: e.target.value })}
                          placeholder="Shown under the field"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
